import type { CustomerGroup } from "@cleanbudget/contract/price";
import { Badge } from "@cleanbudget/ui/components/badge";
import { Button } from "@cleanbudget/ui/components/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@cleanbudget/ui/components/card";
import { IconPencil, IconTrash } from "@tabler/icons-react";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { match } from "ts-pattern";
import { BackLink } from "@/components/back-link";
import { type Column, DataTable } from "@/components/data-table/data-table";
import { PageHeader } from "@/components/page-header";
import { QuantityDiscountEditor } from "@/components/quantity-discount-editor";
import { CustomerGroupForm } from "./-components/customer-group-form";
import { DeleteCustomerGroupDialog } from "./-components/delete-customer-group-dialog";
import {
	customerGroupsQuery,
	useCustomerGroupErrorMessage,
	useCustomerGroupWrites,
} from "./-components/use-customer-groups";

/**
 * Instellingen → Klantgroepen (#56, #58): Consument (fixed, read-only) and the business groups with
 * their default discount, which Richard adds, edits and deletes; and the shop's default quantity
 * discount list, edited with the same list the product editor uses.
 */
export const Route = createFileRoute("/_admin/settings/customer-group/")({
	loader: ({ context }) => context.queryClient.ensureQueryData(customerGroupsQuery()),
	component: CustomerGroupsPage,
});

function CustomerGroupsPage() {
	const { t } = useTranslation("price");
	const { t: tSettings } = useTranslation("settings");
	const { data } = useSuspenseQuery(customerGroupsQuery());
	const writes = useCustomerGroupWrites();
	const errorMessage = useCustomerGroupErrorMessage();
	const [editingId, setEditingId] = useState<string>();
	const editing = data.groups.find((group) => group.id === editingId && group.kind === "business");

	const columns: readonly Column<CustomerGroup>[] = [
		{
			id: "name",
			header: t("groups.column.name"),
			cell: (group) => (
				<span className="flex flex-col gap-0.5">
					<span className="flex flex-wrap items-center gap-2 font-medium">
						{group.name}
						{group.kind === "consumer" ? <Badge variant="secondary">{t("groups.fixed")}</Badge> : null}
					</span>
					{group.kind === "consumer" ? (
						<span className="hidden text-xs whitespace-normal text-muted-foreground md:block">
							{t("groups.fixedDescription")}
						</span>
					) : null}
				</span>
			),
		},
		{
			id: "discount",
			header: t("groups.column.discount"),
			align: "right",
			cell: (group) => (
				<span className="tabular-nums">{t("percent", { percent: group.defaultDiscountPercent })}</span>
			),
		},
		{
			id: "basis",
			header: t("groups.column.basis"),
			secondary: true,
			cell: (group) =>
				match(group.vatBasis)
					.with("inclVat", () => t("groups.basis.inclVat"))
					.with("exclVat", () => t("groups.basis.exclVat"))
					.exhaustive(),
		},
		{
			id: "actions",
			header: <span className="sr-only">{t("groups.column.actions")}</span>,
			align: "right",
			cell: (group) =>
				group.kind === "consumer" ? null : (
					<span className="flex justify-end gap-1">
						<Button
							variant="ghost"
							size="icon-sm"
							aria-label={t("groups.edit.button", { name: group.name })}
							onClick={() => setEditingId(group.id)}
						>
							<IconPencil aria-hidden />
						</Button>
						<DeleteCustomerGroupDialog
							group={group}
							groups={data.groups}
							trigger={
								<Button
									variant="ghost"
									size="icon-sm"
									aria-label={t("groups.delete.button", { name: group.name })}
								>
									<IconTrash aria-hidden />
								</Button>
							}
							onDeleted={() => (editingId === group.id ? setEditingId(undefined) : undefined)}
						/>
					</span>
				),
		},
	];

	return (
		<div className="flex max-w-3xl flex-col gap-6">
			<PageHeader
				back={<BackLink to="/settings">{tSettings("title")}</BackLink>}
				title={t("title")}
				description={t("groups.description")}
			/>
			<DataTable
				label={t("groups.label")}
				columns={columns}
				rows={data.groups}
				rowKey={(group) => group.id}
				empty={<p className="p-6 text-center text-sm text-muted-foreground">{t("groups.empty")}</p>}
			/>
			{editing ? (
				<CustomerGroupForm
					key={editing.id}
					group={editing}
					writes={writes}
					onDone={() => setEditingId(undefined)}
				/>
			) : (
				<CustomerGroupForm writes={writes} />
			)}
			<Card>
				<CardHeader>
					<CardTitle>{t("shop.title")}</CardTitle>
					<CardDescription>{t("shop.description")}</CardDescription>
				</CardHeader>
				<CardContent>
					<QuantityDiscountEditor
						label={t("shop.label")}
						value={data.shopQuantityDiscounts}
						onSave={(quantityDiscounts) => writes.setShopQuantityDiscounts.mutateAsync({ quantityDiscounts })}
						errorMessage={errorMessage}
					/>
				</CardContent>
			</Card>
		</div>
	);
}
